/**
 * AuthClient internal helpers that close over NOTHING in the client closure —
 * split out of client.ts (PR #1813 follow-up 2) so that file stays under the
 * 500-effective-line changed-file cap. These four are pure module-scope
 * functions/types: `isOidcCallbackInProgress` reads only `window`,
 * `Deferred`/`createDeferred` are generic primitives, and `notifyListeners`
 * takes everything it needs as arguments (its only module dependency is
 * `safeWarn`). Nothing here reads `tornDown`, `currentSession`, the scheduler,
 * or any other per-instance state, which is exactly why the move is safe.
 */
import { safeWarn } from "./config.js";

/**
 * True when the current page load is an OIDC authorization-code callback —
 * auth.molaris.ai has redirected the browser back to this app's redirect_uri
 * with `?code=…&state=…` still in the query string. This is exactly the window
 * in which handleCallback owns session establishment (ENG-1753). Reads
 * `window.location` fresh on each call so it flips back to `false` the moment
 * the callback page navigates away (the code/state params are gone).
 */
export function isOidcCallbackInProgress(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") && params.has("state");
  } catch {
    return false;
  }
}

export interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
}

export function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  const promise = new Promise<T>((r) => {
    resolve = r;
  });
  return { promise, resolve };
}

/**
 * ENG-2756 fix round 3 — observer fan-out with PER-LISTENER isolation.
 *
 * Both listener sets are public API for application code. Today's non-test
 * registrations are both onSessionChange — AuthProvider's setState and apps/main's
 * Apollo-link WS-token gate; nothing outside tests registers an onAuthError
 * listener. A bare `for (const l of set) l(arg)` hands a single throwing listener
 * two powers it must not have: it silently drops every listener
 * registered after it, and it unwinds into the EMITTER. The second one was a live
 * bug — emitSession() runs MID-WIPE during sign-out (sign-out-flow.ts's
 * `clearInMemorySession` is the wipe's FIRST step), so an escaping throw aborts
 * `wipeLocalSessionState()` before the synchronous OIDC-record delete, the
 * `removeUser()` race and the signed-in-marker cleanup. The flow's outer catch
 * then re-runs the (idempotent) wipe, which throws at the same listener again and
 * is swallowed — so those three steps never run. Since ENG-2907 the navigation
 * tail and the verdict SURVIVE it (the tail is outside that try/catch and `result`
 * is hoisted), which makes the failure quieter, not smaller: sign-out reports
 * `signed_out` and redirects while the cached OIDC user is still in
 * sessionStorage for the next cold load to re-adopt. Containing the throw is also
 * what frees handleCallback's throw-path re-latch from having to guess whether a
 * throw out of adoptOidcUser meant "the adoption failed" or "the adoption
 * succeeded and a listener threw"; with the throw contained, that guard is
 * unconditional.
 *
 * Only a SYNCHRONOUS throw is containable here: both listener types return void,
 * so nothing ever awaits an `async` listener and its rejection cannot reach this
 * catch. Nothing else handles it either — it surfaces as an UNHANDLED rejection
 * (measured, fix round 4: one per throwing async listener; the emitter itself is
 * unaffected). Put sharply: for an `async` listener the try/catch below is a NO-OP,
 * not a weaker guard — even a throw before its first `await` is handed back as a
 * rejected promise instead of thrown synchronously (measured at 94a04da44).
 *
 * So: register synchronous listeners. Exactly one non-test listener in this repo
 * violates that, knowingly — apps/main/src/apollo-client.ts's `async (session) => …`
 * WS-token gate, one of the two holders named above. It cannot reject TODAY: its
 * body is `if (!session) return; const next = await authClient.getAccessToken();
 * if (next) finish(…)`, `getAccessToken()` has no rejecting path (its near-expiry
 * refresh is try/catch → return null, and its cold-load delegates to
 * getCurrentUser, whose whole body is wrapped in `catch { return null }`), and
 * `finish` only deletes from a Set, clears a timeout and resolves. That is safe by
 * accident of its callee, not by construction: the day getAccessToken() acquires a
 * rejecting path, this becomes an unhandled rejection on every failed handshake.
 * Nothing enforces this directive today — `no-misused-promises` with
 * `checksVoidReturn` would, and is ticketed separately.
 *
 * Logged rather than swallowed — the same non-fatal-error convention as the
 * silent-refresh scheduler's onError callback (in client.ts). This package has
 * no logger abstraction and this is not the change that should introduce one.
 */
export function notifyListeners<T>(
  listeners: Iterable<(arg: T) => void>,
  arg: T,
  label: string,
): void {
  for (const listener of listeners) {
    try {
      listener(arg);
    } catch (err) {
      // Fix round 4 — the catch BODY is protected too, and that protection is
      // load-bearing, not decoration. `console.*` is patchable and routinely
      // patched in the browser (Sentry browser, Datadog RUM and LogRocket all wrap
      // it; none is wired into this repo today, and no apps/*/vite.config.ts drops
      // console calls, so this line ships in every deployed bundle). An
      // unprotected logger throw turns the containment above straight back into an
      // escaping throw — out of the emitter, into handleCallback's catch, onto the
      // unconditional beginTeardown() — tearing down a live, valid session while
      // getAccessToken() still hands out its bearer (measured at d1d4c6f0b, when
      // this was a bare `console.warn` wrapped in its own try/catch here).
      // ENG-2907 shipped `safeWarn` (config.ts) doing exactly that wrap, so the
      // local try/catch is gone in favour of the package-wide helper; the guard
      // now lives in one place instead of at each call site.
      safeWarn(`[auth-client] ${label} listener threw`, err);
    }
  }
}
